import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import ProductsPage from './pages/ProductsPage';
import CustomersPage from './pages/CustomersPage';
import SalesPage from './pages/SalesPage';
import InventoryPage from './pages/InventoryPage';
import ReportsPage from './pages/ReportsPage';
import Dashboard from './components/Dashboard';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import OrdersManagementPage from './pages/OrdersManagementPage';
import CalendarPage from './pages/CalendarPage';
import ExpensesPage from './pages/ExpensesPage';
import AdminPage from './pages/AdminPage';       // 🛡️ SuperAdmin
import ExpiredPage from './pages/ExpiredPage';   // 🚧 Muro de Peaje
import DebtsPage from './pages/DebtsPage';       // 💳 Cuentas por Cobrar
import SucursalesPage from './pages/SucursalesPage'; // 🏢 Mis Sucursales
import EmpleadosPage  from './pages/EmpleadosPage';  // 👤 Cajeros
import ParkingPage    from './pages/ParkingPage';    // 🚗 Parking / Estacionamiento
import PromosPage     from './pages/PromosPage';     // 🎁 Promociones / Sellos
import { getModulosEfectivos, isAdmin, getTipoCuenta } from './hooks/useSucursal';
import './App.css';

// Decodifica el payload de un JWT con soporte correcto para Base64URL
// (JWT usa Base64URL: '-' en vez de '+' y '_' en vez de '/')
const isTokenValid = (token: string): boolean => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return false;
    // Convertir Base64URL → Base64 estándar
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);
    const now = Math.floor(Date.now() / 1000);
    // Válido si tiene exp y no ha expirado
    return payload.exp ? payload.exp > now : true;
  } catch {
    return false;
  }
};

const PrivateRoute = () => {
  const token = localStorage.getItem('token');
  if (!token || !isTokenValid(token)) {
    localStorage.removeItem('token');
    return <Navigate to="/login" />;
  }
  return <Outlet />;
};

const ModuleRoute = ({ moduleName }: { moduleName: string }) => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;

  try {
    const userString = localStorage.getItem('user');
    const user = userString ? JSON.parse(userString) : null;

    // Si es superadmin o no hay usuario parseable (raro), lo dejamos pasar (authAPI lo botará si es fake)
    if (user?.role === 'superadmin') return <Outlet />;

    // 🔒 El Dashboard muestra finanzas globales del negocio: un cajero jamás debe entrar al dashboard
    if (moduleName === 'dashboard' && !isAdmin()) {
      return <Navigate to="/sales" replace />;
    }

    const defaultModules: Record<string, boolean> = {
        dashboard: true, ventas: true, productos: true, clientes: true,
        fiados: true, gastos: false, inventario: false, pedidos: false,
        reportes: false, cronograma: false, sucursales: false, cajeros: false,
        parking: false
    };

    // Módulos EFECTIVOS: intersección entre lo que el Dueño tiene habilitado
    // en su cuenta y lo que la sucursal activa en este dispositivo permite
    const modules = getModulosEfectivos(user?.modules || defaultModules);

    // Compatibilidad y verificación de módulo activo
    const isAllowed = 
      moduleName === 'fiados' ? (modules.fiados ?? modules.clientes) :
      moduleName === 'cajeros' ? (modules.cajeros || modules.sucursales) :
      moduleName === 'parking' ? (modules.parking || user?.tipo_negocio === 'parking') :
      modules[moduleName];

    // Si el módulo está activo, lo dejamos pasar
    if (isAllowed) {
      return <Outlet />;
    }

    if (user?.tipo_negocio === 'parking' || modules.parking) return <Navigate to="/parking" replace />;
    if (isAdmin() && modules.dashboard) return <Navigate to="/dashboard" replace />;
    return <Navigate to="/sales" replace />;
  } catch {
    return <Navigate to="/login" />;
  }
};

// Guard exclusivo para superadmin
const AdminRoute = () => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.role === 'superadmin' ? <Outlet /> : <Navigate to="/dashboard" />;
  } catch {
    return <Navigate to="/login" />;
  }
};

// Guard para áreas exclusivas del Admin del negocio (Sucursales, Cajeros).
// Un cajero o una terminal/kiosco jamás debe poder navegar aquí — se redirige a /sales.
const AdminAreaRoute = () => {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" />;
  return getTipoCuenta() === 'admin' ? <Outlet /> : <Navigate to="/sales" replace />;
};

const MainLayout = () => (
  <div className="main-layout">
      <Sidebar />
      <main className="main-content">
          <Header />
          <Outlet />
      </main>
  </div>
);

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/expired" element={<ExpiredPage />} />

        {/* Ruta exclusiva SuperAdmin (sin Sidebar ni Header) */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminPage />} />
        </Route>

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            {/* Dashboard */}
            <Route element={<ModuleRoute moduleName="dashboard" />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
            </Route>

            {/* Rutas protegidas por Módulo */}
            <Route element={<ModuleRoute moduleName="ventas" />}>
              <Route path="/sales" element={<SalesPage />} />
            </Route>
            
            <Route element={<ModuleRoute moduleName="pedidos" />}>
              <Route path="/orders" element={<OrdersManagementPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="cronograma" />}>
              <Route path="/calendar" element={<CalendarPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="productos" />}>
              <Route path="/products" element={<ProductsPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="clientes" />}>
              <Route path="/customers" element={<CustomersPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="fiados" />}>
              <Route path="/fiados" element={<DebtsPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="inventario" />}>
              <Route path="/inventory" element={<InventoryPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="gastos" />}>
              <Route path="/expenses" element={<ExpensesPage />} />
            </Route>

            <Route element={<ModuleRoute moduleName="reportes" />}>
              <Route path="/reports" element={<ReportsPage />} />
            </Route>

            {/* 🚗 Módulo Parking / Estacionamiento */}
            <Route element={<ModuleRoute moduleName="parking" />}>
              <Route path="/parking" element={<ParkingPage />} />
            </Route>

            {/* Sucursales y Cajeros — exclusivo del Admin del negocio */}
            <Route element={<AdminAreaRoute />}>
              <Route element={<ModuleRoute moduleName="sucursales" />}>
                <Route path="/sucursales" element={<SucursalesPage />} />
              </Route>
              <Route element={<ModuleRoute moduleName="cajeros" />}>
                <Route path="/cajeros" element={<EmpleadosPage />} />
              </Route>
              {/* 🎁 Módulo Promos (solo admin/dueño) */}
              <Route path="/promos" element={<PromosPage />} />
            </Route>


            <Route path="*" element={<Navigate to={(() => {
              try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                if (u.tipo_negocio === 'parking' || u.modules?.parking) return "/parking";
              } catch {}
              return isAdmin() ? "/dashboard" : "/sales";
            })()} replace />} />
          </Route>
        </Route>
      </Routes>
    </Router>
  );
}

export default App;